/**
 * IANA timezone catalog — sourced live from the runtime via
 * `Intl.supportedValuesOf('timeZone')` when available, otherwise from a
 * static fallback covering the 200+ most-common zones across every region.
 *
 * Returned grouped by region prefix (Africa, America, Antarctica, Asia, etc.)
 * so the picker can render headers without re-parsing every entry.
 */

const FALLBACK_TIMEZONES: ReadonlyArray<string> = [
  'UTC',
  'Africa/Abidjan',
  'Africa/Accra',
  'Africa/Algiers',
  'Africa/Cairo',
  'Africa/Casablanca',
  'Africa/Johannesburg',
  'Africa/Lagos',
  'Africa/Nairobi',
  'Africa/Tunis',
  'America/Adak',
  'America/Anchorage',
  'America/Anguilla',
  'America/Antigua',
  'America/Argentina/Buenos_Aires',
  'America/Asuncion',
  'America/Barbados',
  'America/Belize',
  'America/Bogota',
  'America/Caracas',
  'America/Cayman',
  'America/Chicago',
  'America/Costa_Rica',
  'America/Cuiaba',
  'America/Denver',
  'America/Detroit',
  'America/El_Salvador',
  'America/Fortaleza',
  'America/Godthab',
  'America/Guatemala',
  'America/Guayaquil',
  'America/Halifax',
  'America/Havana',
  'America/Indianapolis',
  'America/Jamaica',
  'America/Juneau',
  'America/La_Paz',
  'America/Lima',
  'America/Los_Angeles',
  'America/Managua',
  'America/Manaus',
  'America/Martinique',
  'America/Mazatlan',
  'America/Mexico_City',
  'America/Monterrey',
  'America/Montevideo',
  'America/Nassau',
  'America/New_York',
  'America/Noronha',
  'America/Panama',
  'America/Phoenix',
  'America/Port-au-Prince',
  'America/Port_of_Spain',
  'America/Puerto_Rico',
  'America/Regina',
  'America/Santiago',
  'America/Santo_Domingo',
  'America/Sao_Paulo',
  'America/St_Johns',
  'America/Tegucigalpa',
  'America/Tijuana',
  'America/Toronto',
  'America/Vancouver',
  'America/Whitehorse',
  'America/Winnipeg',
  'Asia/Almaty',
  'Asia/Amman',
  'Asia/Baghdad',
  'Asia/Bahrain',
  'Asia/Baku',
  'Asia/Bangkok',
  'Asia/Beirut',
  'Asia/Bishkek',
  'Asia/Colombo',
  'Asia/Damascus',
  'Asia/Dhaka',
  'Asia/Dubai',
  'Asia/Hong_Kong',
  'Asia/Ho_Chi_Minh',
  'Asia/Irkutsk',
  'Asia/Istanbul',
  'Asia/Jakarta',
  'Asia/Jerusalem',
  'Asia/Kabul',
  'Asia/Karachi',
  'Asia/Kathmandu',
  'Asia/Kolkata',
  'Asia/Krasnoyarsk',
  'Asia/Kuala_Lumpur',
  'Asia/Kuwait',
  'Asia/Manila',
  'Asia/Muscat',
  'Asia/Nicosia',
  'Asia/Pyongyang',
  'Asia/Qatar',
  'Asia/Riyadh',
  'Asia/Seoul',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Asia/Taipei',
  'Asia/Tashkent',
  'Asia/Tbilisi',
  'Asia/Tehran',
  'Asia/Tokyo',
  'Asia/Ulaanbaatar',
  'Asia/Vladivostok',
  'Asia/Yangon',
  'Asia/Yekaterinburg',
  'Asia/Yerevan',
  'Atlantic/Azores',
  'Atlantic/Bermuda',
  'Atlantic/Canary',
  'Atlantic/Cape_Verde',
  'Atlantic/Reykjavik',
  'Atlantic/South_Georgia',
  'Australia/Adelaide',
  'Australia/Brisbane',
  'Australia/Darwin',
  'Australia/Hobart',
  'Australia/Lord_Howe',
  'Australia/Melbourne',
  'Australia/Perth',
  'Australia/Sydney',
  'Europe/Amsterdam',
  'Europe/Athens',
  'Europe/Belgrade',
  'Europe/Berlin',
  'Europe/Brussels',
  'Europe/Bucharest',
  'Europe/Budapest',
  'Europe/Chisinau',
  'Europe/Copenhagen',
  'Europe/Dublin',
  'Europe/Helsinki',
  'Europe/Istanbul',
  'Europe/Kaliningrad',
  'Europe/Kiev',
  'Europe/Lisbon',
  'Europe/London',
  'Europe/Madrid',
  'Europe/Minsk',
  'Europe/Moscow',
  'Europe/Oslo',
  'Europe/Paris',
  'Europe/Prague',
  'Europe/Riga',
  'Europe/Rome',
  'Europe/Samara',
  'Europe/Sofia',
  'Europe/Stockholm',
  'Europe/Tallinn',
  'Europe/Vienna',
  'Europe/Vilnius',
  'Europe/Warsaw',
  'Europe/Zurich',
  'Indian/Mahe',
  'Indian/Maldives',
  'Indian/Mauritius',
  'Indian/Reunion',
  'Pacific/Apia',
  'Pacific/Auckland',
  'Pacific/Chatham',
  'Pacific/Easter',
  'Pacific/Fiji',
  'Pacific/Galapagos',
  'Pacific/Gambier',
  'Pacific/Guadalcanal',
  'Pacific/Guam',
  'Pacific/Honolulu',
  'Pacific/Marquesas',
  'Pacific/Midway',
  'Pacific/Niue',
  'Pacific/Pago_Pago',
  'Pacific/Port_Moresby',
  'Pacific/Tahiti',
  'Pacific/Tongatapu',
];

interface RuntimeWithIntlSupportedValues {
  supportedValuesOf?: (input: 'timeZone') => string[];
}

function fetchTimezones(): readonly string[] {
  try {
    const supported = (Intl as unknown as RuntimeWithIntlSupportedValues).supportedValuesOf;
    if (typeof supported === 'function') {
      const list = supported('timeZone');
      if (Array.isArray(list) && list.length > 0) return list;
    }
  } catch {
    /* fall through */
  }
  return FALLBACK_TIMEZONES;
}

let _cache: readonly string[] | null = null;
function listTimezones(): readonly string[] {
  if (_cache) return _cache;
  const sorted = [...fetchTimezones()].sort((a, b) => a.localeCompare(b));
  _cache = sorted;
  return sorted;
}

export interface TimezoneGroup {
  region: string;
  zones: readonly string[];
}

/** Group zones by region prefix (text before the first slash). UTC + ETC bucket separately. */
export function groupedTimezones(): readonly TimezoneGroup[] {
  const all = listTimezones();
  const groups = new Map<string, string[]>();
  for (const tz of all) {
    const slashIdx = tz.indexOf('/');
    const region = slashIdx === -1 ? 'Other' : tz.slice(0, slashIdx);
    let bucket = groups.get(region);
    if (!bucket) {
      bucket = [];
      groups.set(region, bucket);
    }
    bucket.push(tz);
  }
  return [...groups.entries()]
    .map(([region, zones]) => ({ region, zones: zones.sort((a, b) => a.localeCompare(b)) }))
    .sort((a, b) => a.region.localeCompare(b.region));
}

/** Detect the operator's host timezone, with a safe UTC fallback. */
export function detectHostTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof tz === 'string' && tz.length > 0 ? tz : 'UTC';
  } catch {
    return 'UTC';
  }
}
